import { queryOptions, useQuery } from '@tanstack/react-query';

import type { UserInboxDto } from '@/api/core-api/types.gen';

import { messagesControllerGetUserInbox } from '@/api/core-api/sdk.gen';
import { getAxiosErrorStatus } from '@/utils/error';
import { getClient } from '@/utils/get-api-client';

import { queryKeys } from './query-keys';

/**
 * Poll cadence for the nav badge. Matches the legacy participant app's
 * 5-minute message check; anything faster is wasted load for a passive
 * indicator. On the Messages page itself, realtime invalidation (via
 * `invalidateParticipantMessaging`) refreshes the badge instantly.
 */
const INBOX_REFETCH_INTERVAL_MS = 5 * 60 * 1000;

/**
 * What the badge sees while the participant has no messaging identity yet:
 * core-api answers the inbox call with a 404 until messaging first touches
 * the user. That's a deterministic state, not a failure.
 */
const EMPTY_INBOX: UserInboxDto = { read: true, count: 0, unreadCount: 0 };

/**
 * Inbox summary query for the participant Messages nav badge.
 *
 * Prefetched from the authenticated layout `beforeLoad` (with the signed-in
 * user id for server auth) so SSR/hydration already has data and the badge
 * doesn't pop in after paint. `staleTime` matches the poll window so soft
 * remounts don't refetch and briefly treat missing data as "no unread".
 */
export function participantInboxQueryOptions(userId?: string) {
  return queryOptions({
    queryKey: queryKeys.participantInbox.mine,
    queryFn: async (): Promise<UserInboxDto> => {
      const response = await messagesControllerGetUserInbox({
        client: getClient(userId),
      });

      if (response.error || !response.data) {
        // 404 = no messaging identity provisioned for this user (every
        // participant polls this now, identity or not). Settle on an empty
        // inbox instead of a permanent error state; the interval keeps
        // polling, so the badge starts working once the identity exists.
        // Anything else stays an error so transient failures are visible
        // and self-correct on the next poll.
        if (getAxiosErrorStatus(response) === 404) {
          return EMPTY_INBOX;
        }
        throw new Error('Failed to load inbox');
      }

      return response.data;
    },
    refetchInterval: INBOX_REFETCH_INTERVAL_MS,
    // Keep the last known count across background refetches/invalidations so
    // the badge doesn't disappear while the next response is in flight.
    staleTime: INBOX_REFETCH_INTERVAL_MS,
    placeholderData: (previous) => previous,
    // A nav badge is not worth retry noise — the next poll self-corrects.
    retry: false,
  });
}

/**
 * The signed-in participant's cross-project inbox summary — unread
 * conversation count for the Messages nav badge. Scoped by the caller's
 * messaging identity (no team needed).
 *
 * A participant whose messaging identity was never provisioned resolves to
 * an empty inbox (the endpoint 404s) rather than an error; remaining errors
 * are silent by design — no badge, not a broken nav.
 */
export function useParticipantInbox() {
  return useQuery(participantInboxQueryOptions());
}
